import { DescriptionAndTags, IModelSelector, NameInput } from "components/actions/forms";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { sandboxApi } from "services/sandbox-api/api";
import { IModelInfo } from "services/SandboxIModels";
import { useAppDispatch, useAppSelector } from "store";
import { sandboxActionInvoked } from "store/features";
import { CodeShareMetadata } from "types";
import { createUniqueName } from "utils/CustomNameGenerator";
import { Button, Dialog } from "@itwin/itwinui-react";
import { FormSpinner } from "../FormSpinner";
import { TemplateList } from "./TemplateList";
import styles from "./TemplateSelectionDialog.module.css";

interface TemplateSelectionDialogProps {
  onConfirm: (sandbox: CodeShareMetadata) => void;
  onClose: () => void;
}

interface FormData extends FieldValues {
  name?: string;
  description?: string;
  imodels?: IModelInfo[];
};

export const TemplateSelectionDialog = ({ onConfirm, onClose }: TemplateSelectionDialogProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [templateSelected, setTemplateSelected] = useState<CodeShareMetadata>();
  const [getSandbox, { currentData: sandbox, }] = sandboxApi.useLazyCodeShareQuery();
  const [forkSandbox, { isLoading: sandboxForking }] = sandboxApi.useCodeShareForkMutation()
  const dispatch = useAppDispatch();
  const [sandboxLoading, setSandboxLoading] = useState(false);
  const sandboxUser = useAppSelector((state) => state.auth.sandboxUser);
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<FormData>();

  const onClick = (sandbox: CodeShareMetadata) => {
    setTemplateSelected(sandbox);
    setSandboxLoading(true);
    getSandbox({ codeshareId: sandbox.id, userId: sandbox.userId })
      .unwrap()
      .then(() => setSandboxLoading(false));
  };

  const onNext = () => {
    if (templateSelected && sandbox) {
      reset({
        name: createUniqueName(),
        description: templateSelected.description,
        imodels: sandbox.allowedImodels
      });
    }
    setActiveStep(1);
  };

  const onSubmit = (fields: FormData) => {
    if (sandbox)
      forkSandbox({
        forkCodeshareId: sandbox.id,
        forkUserId: sandbox.userId,
        name: fields.name!,
        description: fields.description,
        iModels: fields.imodels ? fields.imodels.map(item => item.name) : undefined
      })
        .unwrap()
        .then((forkedSandox) => onConfirm(forkedSandox))
        .finally(() => setTimeout(onClose, 200));
  };

  const onCancel = () => {
    setIsModalOpen(false);
    setTimeout(onClose, 200);
  };

  useEffect(() => {
    setTimeout(() => setIsModalOpen(true), 5);
  }, []);

  useEffect(() => {
    if (!sandboxUser) {
      onCancel();
      dispatch(sandboxActionInvoked({ type: "set-username", args: { actionResumeType: "create" } }));
    }
  }, [dispatch, sandboxUser]);

  if (!sandboxUser)
    return null;

  const stepOne = () => {
    return (
      <div className={styles.tiles}>
        <TemplateList onSelected={onClick} />
      </div>
    )
  };

  const stepTwo = () => {
    return (
      <div className={styles.formFields}>
        <NameInput name="name" control={control} userName={sandboxUser.username} />
        <DescriptionAndTags name="description" control={control} />
        <IModelSelector name="imodels" control={control} />
      </div>
    )
  };

  return (
    <Dialog isOpen={isModalOpen} onClose={onCancel} closeOnEsc closeOnExternalClick preventDocumentScroll trapFocus setFocus isDismissible>
      <Dialog.Backdrop />
      <Dialog.Main data-testid="atp-create-sandbox-dialog">
        <Dialog.TitleBar titleText="Creating new Sandbox" />
        <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <Dialog.Content className={styles.layout}>
            {activeStep === 0 && stepOne()}
            {activeStep === 1 && stepTwo()}
          </Dialog.Content>
          <Dialog.ButtonBar>
            {sandboxLoading && <FormSpinner message="Loading template..." />}
            {sandboxForking && <FormSpinner message="Saving..." />}
            {activeStep === 0 && <Button styleType="high-visibility" onClick={onNext} disabled={!templateSelected || sandboxLoading || !sandbox} data-testid="atp-next-btn">Next</Button>}
            {activeStep === 1 && <Button styleType="high-visibility" type="submit" disabled={Object.keys(errors).length > 0} data-testid="atp-create-btn">Create</Button>}
            <Button onClick={onCancel}>Cancel</Button>
          </Dialog.ButtonBar>
        </form>
      </Dialog.Main>
    </Dialog>
  )
};

